import { produce } from 'immer';

import {
  OmniElement,
  OmniStyleElement,
  OmniIconElement,
  classMap,
  html,
  css,
  nothing,
  repeat,
  ref,
  createRef,
} from 'omni-ui';

import { om2alert } from 'omni-campaign-ui';

import { Editor } from '@tiptap/core';
import Mention from '@tiptap/extension-mention';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import Placeholder from '@tiptap/extension-placeholder';

import { MicrosoftAuthenticationHandler } from 'omni-teams-sdk';

import { debounceAsync } from '../helpers/utils.js';

OmniStyleElement.register();
OmniIconElement.register();

/**
 * Component for campaign's rich text editor based on the TipTap library. It defers state management to TipTap, sending
 * change events with each update. If you must force a content update from another component, or if reusing the same instance,
 * you can call the public `setContent` method on the instance of the element.
 */
export class CampaignWYSIWYG extends OmniElement {
  /**
   * @property {Boolean} displayOnly - tracks whether user can edit the contents of the editor. Used to make CampaignWYSIWYG a display of the formatted html string
   * @property {Boolean} overflowVisible - tracks whether we want to show overflow or hide it with CSS rule (:host[overflowed])
   * @property {Boolean} overflowed - tracks if editor has overflown the containing div
   * @property {Array.<'undo'|'redo'|'bold'|'b'|'italic'|'i'|'underline'|'u'|'bulletList'|'bl'|'orderedList'|'ol'>} enabledExtensions - a list of extensions to enable for rich-text functionality
   * @property {Boolean} shouldAutofocus - tracks whether editor should be automatically focused within when switching to a view that contains the editor
   * @property {String} placeholder - placeholder text for the editor
   * @property {object} menuButtons - object containing the buttons to be displayed in the editor toolbar
   * @property {Boolean} enableMentions - tracks whether editor should be allow the use of mentions
   * @property {Boolean} showingMentions - tracks whether editor should display a list of teams members available to mention
   * @property {object} mentionPosition - object containing bounding rect position of the start of the mention("@")
   * @property {Object []} mentionMatches - filtered list of teams users matching the name searched
   * @property {Number} mentionItemSelected - index of mentionMatchs to track currently selected
   */
  static get properties() {
    return {
      displayOnly: { attribute: 'display-only', type: Boolean, reflect: true },
      overflowVisible: {
        attribute: 'overflow-visible',
        type: Boolean,
        reflect: true,
      },
      overflowed: { type: Boolean, reflect: true },
      enabledExtensions: {
        type: Array,
      },
      shouldAutofocus: { type: Boolean },

      placeholder: { type: String },
      menuButtons: { type: Object, state: true },

      enableMentions: {
        attribute: 'enable-mentions',
        type: Boolean,
        reflect: true,
      },
      showingMentions: { type: Boolean, state: true },
      mentionPosition: { type: Object, state: true },
      mentionMatches: { type: Array, state: true },
      mentionItemSelected: { type: Number },
    };
  }

  /**
   * CampaignWYSIWYG module styles
   */
  static get styles() {
    return [
      super.styles,
      css`
        :host strong {
          color: inherit;
        }

        /* Parent container needs have a height set */
        :host,
        omni-style.omni,
        .wysiwyg {
          height: 100%;
          width: 100%;
        }

        .wysiwyg {
          display: flex;
          flex-flow: column nowrap;
          position: relative;
        }

        .ProseMirror {
          word-break: break-word;
        }

        .is-editing .ProseMirror p.is-editor-empty:first-child::before {
          color: var(--color-shark);
          content: attr(data-placeholder);
          float: left;
          height: 0;
          pointer-events: none;
        }

        .is-editing .ProseMirror {
          height: 100%;
          border-radius: 0 0 0.43rem 0.43rem;
          border: solid thin var(--color-pale-grey-three);
          background-color: var(--color-pale-grey-two);
          padding: 1.07rem 1.43rem;
          overflow-y: scroll;
          white-space: pre-wrap;
        }

        .is-editing .ProseMirror:focus-visible {
          outline: solid thin var(--color-primary);
        }

        div.menu-container {
          display: flex;
          flex-wrap: nowrap;
          justify-content: flex-start;
          align-items: center;
          gap: 0.57rem;
          padding: 0.36rem 0.71rem;
          border: solid thin var(--color-pale-grey-three);
          border-bottom: none;
          border-radius: 0.43rem 0.43rem 0 0;
          background-color: var(--color-white);
        }
        .menu-container button.button {
          width: 2.14rem;
          height: 2.14rem;
          padding: 0;
          border-radius: 0.29rem;
          align-content: center;
          background-color: var(--color-white);
          border: none;
        }

        omni-icon::part(icon) {
          width: 1.07rem;
          margin: 0 auto;
        }

        :host([overflowed]) {
          -webkit-mask-image: linear-gradient(
            180deg,
            var(--black) 60%,
            transparent
          );
          mask-image: linear-gradient(180deg, var(--black) 60%, transparent);
        }
        .omni ul {
          list-style: disc;
        }

        .omni ul,
        .omni ol {
          padding-left: 1.29rem;
        }

        .mentionList {
          position: absolute;
          top: var(--mention-list-y);
          left: var(--mention-list-x);
          overflow-y: auto;
          max-height: 200px;
          z-index: 2;
        }
        .omni .mention {
          font-weight: bold;
        }
        .omni .mention-item-selected {
          background-color: var(--color-whitesmoke);
          color: var(--color-gray-0a);
        }
        .omni .dropdown-content {
          padding: 0;
        }
      `,
    ];
  }

  /**
   * Initializes {@link #content}, {@link #displayOnly}, {@link #shouldAutofocus} and {@link #placeholder}
   */
  constructor() {
    super();
    this.wysiwygRef = createRef();
    this.initialContent = '';
    this.displayOnly = false;
    this.overflowVisible = false;
    this.shouldAutofocus = false;
    this.placeholder = 'Enter text';
    this.menuButtons = {};

    this.enableMentions = false;

    this.mentionListRef = createRef();
    this.showingMentions = false;
    this.mentionPosition = {};
    this.mentionMatches = [];
    this.msAuth = new MicrosoftAuthenticationHandler();
    this.suggestionConfig = this.#configureSuggestion;
    this.mentionItemSelected = 0;
  }

  /**
   * Allow externally setting the content of this controller to plain text. Used by
   * CampaignAssistField.
   *
   * @param {string} text - The plain text content that should be set
   */
  set value(text) {
    this.editor.commands.clearContent();
    this.editor.commands.insertContent(text);
  }

  #configureSuggestion = {
    allowSpaces: true,

    items: debounceAsync(async ({ query }) => {
      if (!query) return;
      try {
        const results = await this.msAuth.getPeople(query);

        // eslint-disable-next-line consistent-return
        return results.map(result => ({
          PersonGUID: result.id,
          Name: result.displayName,
        }));
      } catch (e) {
        let message = e;
        if (e instanceof Response) {
          message = await e.text();
        }
        console.warn('Failed to get team members.', e);
        om2alert(
          String(message) ||
            'Failed to get team members. Please try again later.',
        );
      }
    }, 1000),

    render: () => ({
      onStart: () => {
        this.showingMentions = true;
        this.mentionItemSelected = 0;
      },

      onUpdate: props => {
        this.mentionMatches = props.items;
        this.mentionCommand = props.command;
        this.mentionPosition = props.clientRect();
        this.#setMentionListPosition();
      },

      onKeyDown: props => {
        if (
          !['escape', 'enter', 'arrowup', 'arrowdown'].includes(
            props.event.key?.toLowerCase(),
          )
        )
          return false;

        if (props.event.key === 'Escape') {
          this.mentionMatches = [];
          this.mentionPosition = {};
          this.showingMentions = false;
        }
        if (props.event.key === 'Enter') {
          this.#selectMention();
        }
        if (props.event.key === 'ArrowUp') {
          if (this.mentionItemSelected > 0) {
            this.mentionItemSelected -= 1;
          } else {
            this.mentionItemSelected = this.mentionMatches.length - 1;
          }
        }
        if (props.event.key === 'ArrowDown') {
          if (this.mentionMatches.length - 1 > this.mentionItemSelected) {
            this.mentionItemSelected += 1;
          } else {
            this.mentionItemSelected = 0;
          }
        }
        const i = this.mentionItemSelected;
        const item = this.shadowRoot.querySelector(
          `.mention-item:nth-child(${i + 1})`,
        );

        this.mentionListRef.value.getBoundingClientRect();
        const { top: mentionListTop, height: mentionListHeight } =
          this.mentionListRef.value.getBoundingClientRect();

        item.getBoundingClientRect();
        const { top: itemTop } = item.getBoundingClientRect();

        if (
          mentionListTop + mentionListHeight < itemTop ||
          mentionListTop > itemTop
        ) {
          this.mentionListRef.value.scrollTop = item.offsetTop;
        }

        return true;
      },

      onExit: () => {
        this.mentionMatches = [];
        this.mentionPosition = {};
        this.showingMentions = false;
      },
    }),
  };

  #setupMenuButtons = () => {
    // Manually specifying actions is necessary to track changes in toggle state for buttons like numberedList and bulletList
    const defaultExtensions = {
      undo: {
        icon: 'omni:editor:undo',
        method: 'undo',
        tooltip: 'Undo',
        order: 0,
      },
      redo: {
        icon: 'omni:editor:redo',
        method: 'redo',
        tooltip: 'Redo',
        order: 1,
      },
      bold: {
        icon: 'omni:editor:bold',
        method: 'toggleBold',
        tooltip: 'Bold',
        order: 2,
      },
      italic: {
        icon: 'omni:editor:italics',
        method: 'toggleItalic',
        tooltip: 'Italize',
        order: 3,
      },
      underline: {
        icon: 'omni:editor:underline',
        method: 'toggleUnderline',
        tooltip: 'Underline',
        order: 4,
      },
      bulletList: {
        icon: 'omni:editor:bulletList',
        method: 'toggleBulletList',
        tooltip: 'Bullet List',
        order: 5,
      },
      orderedList: {
        icon: 'omni:editor:numberList',
        method: 'toggleOrderedList',
        tooltip: 'Number List',
        order: 6,
      },
    };

    this.menuButtons = this.enabledExtensions?.length
      ? this.enabledExtensions.reduce((acc, extension) => {
          let name = extension;
          switch (extension.toLowerCase()) {
            case 'bl':
              name = 'bulletList';
              break;
            case 'ol':
              name = 'orderedList';
              break;
            case 'b':
              name = 'bold';
              break;
            case 'i':
              name = 'italic';
              break;
            case 'u':
              name = 'underline';
              break;
            default:
              break;
          }

          // eslint-disable-next-line no-param-reassign
          acc[name] = defaultExtensions[name];
          return acc;
        }, {})
      : defaultExtensions;
  };

  /**
   * Creates an instance of Tiptap Editor with the desired extensions and the values for the editors properties shouldAutofocus, displayOnly, and content. Sets {@link #handleContentChange} as the callback for the editor's on update event.
   */
  async firstUpdated() {
    this.#setupMenuButtons();
    const defaultExtensions = [
      StarterKit.configure(),
      Underline,
      Placeholder.configure({
        placeholder: this.placeholder,
      }),
    ];

    if (this.enableMentions) {
      defaultExtensions.push(
        Mention.configure({
          HTMLAttributes: {
            class: 'mention',
          },
          suggestion: this.suggestionConfig,
        }),
      );

      /**
       * Initialize Microsoft auth handler
       */

      await this.msAuth.init();
    }

    this.editor = new Editor({
      element: this.wysiwygRef.value,

      extensions: defaultExtensions,
      autofocus: this.shouldAutofocus,
      editable: !this.displayOnly,
      content: this.initialContent,
    });

    this.editor.on('update', this.#handleContentChange);
    this.editor.on('selectionUpdate', this.#updateButtonStates);

    // When this component changes dimensions re-check for overflow
    new ResizeObserver(this.#detectOverflow).observe(this);
    this.#detectOverflow();
  }

  #selectMention = () => {
    const teamsUser = this.mentionMatches[this.mentionItemSelected];
    this.mentionCommand({ id: teamsUser.PersonGUID, label: teamsUser.Name });
  };

  #setMentionListPosition = () => {
    const { left: containerLeft, top: containerTop } =
      this.getBoundingClientRect();

    this.style.setProperty(
      '--mention-list-x',
      `${this.mentionPosition.left - containerLeft}px`,
    );

    this.style.setProperty(
      '--mention-list-y',
      `${
        this.mentionPosition.top - containerTop + this.mentionPosition.height
      }px`,
    );
  };

  /**
   * Public method for use outside of the component with ref directive
   * to force an editor content update
   * @param {string} content - string being passed into editor
   */
  setContent = content => {
    this.editor.commands.setContent(content);
  };

  /**
   * Grabs the content of the editor as an HTML string, checks if its an empty <p> tag, and then dispatch the content as the payload for change event
   * @param {*} editor - instance of the editor
   */
  #handleContentChange = ({ editor }) => {
    let content = editor.getJSON();
    const htmlContent = editor.getHTML();
    const textContent = editor.view.dom.innerText;
    if (editor.getHTML().replace(/\s/g, '') === '<p></p>') content = '';
    this.dispatchNewEvent('change', {
      detail: { value: content, html: htmlContent, text: textContent },
    });
  };

  /**
   * Detect overflow and update property that is reflected as html attribute
   */
  #detectOverflow = async () => {
    if (this.overflowVisible) return;
    await this.updateComplete; // Wait for next render to occur (if any) before continuing
    const { value: wysiwygElem } = this.wysiwygRef;
    if (wysiwygElem) {
      this.overflowed =
        wysiwygElem.clientHeight < wysiwygElem.firstElementChild.clientHeight;
    }
  };

  #updateButtonStates = () => {
    this.menuButtons = produce(this.menuButtons, draft => {
      Object.keys(draft).forEach(name => {
        draft[name].active = this.editor.isActive(name);
      });
    });
  };

  #handleMenuButtonClick = method => {
    this.editor.chain().focus()[method]().run();
    this.#updateButtonStates();
  };

  /**
   * @returns html for the menu bar and menu buttons for CampaignWYSIWYG
   */
  get #menubarTemplate() {
    return html`
      <div class="menu-container">
        ${repeat(
          Object.keys(this.menuButtons).sort(
            (a, b) => this.menuButtons[a].order - this.menuButtons[b].order,
          ),
          btn => btn,
          btnKey => {
            const btn = this.menuButtons[btnKey];
            return html`
              <omni-tooltip>
                <button
                  class="button ${classMap({ 'is-active': btn.active })}"
                  slot="invoker"
                  @click=${() => {
                    this.#handleMenuButtonClick(btn.method);
                  }}
                >
                  <omni-icon icon-id=${btn.icon} class="is-size-2"></omni-icon>
                </button>
                <div slot="content">${btn.tooltip}</div>
              </omni-tooltip>
            `;
          },
        )}
      </div>
    `;
  }

  /**
   * @returns {TemplateResult} main HTML to be rerendered on the DOM
   */
  render() {
    return html`
      <omni-style>
        <div
          ${ref(this.wysiwygRef)}
          class="wysiwyg ${classMap({ 'is-editing': !this.displayOnly })}"
        >
          ${!this.displayOnly ? this.#menubarTemplate : nothing}
          <div ${ref(this.mentionListRef)} class="mentionList dropdown-content">
            ${this.mentionMatches
              ? repeat(
                  this.mentionMatches,
                  match => match.PersonGUID,
                  (match, idx) =>
                    html`<li
                      class=${classMap({
                        'mention-item-selected':
                          this.mentionItemSelected === idx,
                        'dropdown-item': true,
                        'mention-item': true,
                      })}
                      @mouseover=${() => {
                        this.mentionItemSelected = idx;
                      }}
                      @focus=${e => e.target.mouseover()}
                      @click=${this.#selectMention}
                    >
                      ${match.Name}
                    </li>`,
                )
              : nothing}
          </div>
        </div>
      </omni-style>
    `;
  }
}

OmniElement.register('campaign-wysiwyg', CampaignWYSIWYG);
